import { Injectable } from '@angular/core';
import { SupabaseClient } from '@supabase/supabase-js';
import { SupabaseService } from '../../../core/services/supabase.service';
import { BookmarkInput, CultureConsumptionInput, CultureItemInput, CultureRepository } from '../domain/culture.repository';
import { BookmarkStatus, CultureBookmark, CultureConsumption, CultureItem, CulturePlan, BookDetails, MovieDetails, SeriesSeason, CultureListEntry, CultureListReference } from '../models/culture.model';

@Injectable()
export class SupabaseCultureRepository extends CultureRepository {
  private readonly client: SupabaseClient;

  constructor(supabase: SupabaseService) {
    super();
    this.client = supabase.client;
  }

  async listItems(): Promise<CultureItem[]> {
    const { data, error } = await this.client.from('culture_items')
      .select('id,type,title,source,external_id,created_at,updated_at')
      .order('title');
    if (error) throw error;
    return (data ?? []).map(row => this.mapItem(row));
  }

  async createItem(input: CultureItemInput): Promise<CultureItem> {
    const { data, error } = await this.client.from('culture_items').insert({
      title: input.title, type: input.type, source: input.source ?? null,
      external_id: input.externalId ?? null
    }).select('id,type,title,source,external_id,created_at,updated_at').single();
    if (error) throw error;
    return this.mapItem(data);
  }

  async updateItem(id: number, title: string): Promise<void> {
    const { error } = await this.client.from('culture_items').update({ title }).eq('id', id);
    if (error) throw error;
  }

  async getBookDetails(itemId: number): Promise<BookDetails | null> {
    const { data, error } = await this.client.from('culture_books')
      .select('item_id,author,category,theme,publication_year,language,pages')
      .eq('item_id', itemId).maybeSingle();
    if (error) throw error;
    if (!data) return null;
    return {
      itemId: data.item_id, author: data.author, category: data.category,
      theme: data.theme, publicationYear: data.publication_year,
      language: data.language, pages: data.pages
    };
  }

  async saveBookDetails(details: BookDetails): Promise<void> {
    const { error } = await this.client.from('culture_books').upsert({
      item_id: details.itemId, author: details.author, category: details.category,
      theme: details.theme, publication_year: details.publicationYear,
      language: details.language, pages: details.pages
    }, { onConflict: 'item_id' });
    if (error) throw error;
  }

  async getMovieDetails(itemId: number): Promise<MovieDetails | null> {
    const { data, error } = await this.client.from('culture_movies')
      .select('item_id,release_date,director,genre,country').eq('item_id', itemId).maybeSingle();
    if (error) throw error;
    if (!data) return null;
    return {
      itemId: data.item_id, releaseDate: data.release_date,
      director: data.director, genre: data.genre, country: data.country
    };
  }

  async saveMovieDetails(details: MovieDetails): Promise<void> {
    const { error } = await this.client.from('culture_movies').upsert({
      item_id: details.itemId, release_date: details.releaseDate,
      director: details.director, genre: details.genre, country: details.country
    }, { onConflict: 'item_id' });
    if (error) throw error;
  }

  async deleteItem(id: number): Promise<void> {
    const { error } = await this.client.from('culture_items').delete().eq('id', id);
    if (error) throw error;
  }

  async listSeasons(seriesId: number): Promise<SeriesSeason[]> {
    const { data, error } = await this.client.from('culture_seasons')
      .select('id,series_id,season_number,episode_count').eq('series_id', seriesId).order('season_number');
    if (error) throw error;
    return (data ?? []).map(row => ({
      id: row.id, seriesId: row.series_id, seasonNumber: row.season_number, episodeCount: row.episode_count
    }));
  }

  async addSeason(seriesId: number, seasonNumber: number, episodeCount: number | null): Promise<SeriesSeason> {
    const { data, error } = await this.client.from('culture_seasons')
      .insert({ series_id: seriesId, season_number: seasonNumber, episode_count: episodeCount })
      .select('id,series_id,season_number,episode_count').single();
    if (error) throw error;
    return { id: data.id, seriesId: data.series_id, seasonNumber: data.season_number, episodeCount: data.episode_count };
  }

  async listConsumptions(): Promise<CultureConsumption[]> {
    const { data, error } = await this.client.from('culture_consumptions')
      .select('id,item_id,season_id,started_on,finished_on,rating,comment').order('created_at', { ascending: false });
    if (error) throw error;
    return (data ?? []).map(row => ({
      id: row.id, itemId: row.item_id, seasonId: row.season_id,
      startedOn: row.started_on, finishedOn: row.finished_on, rating: row.rating, comment: row.comment
    }));
  }

  async addConsumption(input: CultureConsumptionInput): Promise<CultureConsumption> {
    const { data, error } = await this.client.from('culture_consumptions').insert({
      item_id: input.itemId ?? null, season_id: input.seasonId ?? null,
      started_on: input.startedOn ?? null, finished_on: input.finishedOn ?? null,
      rating: input.rating ?? null, comment: input.comment ?? null
    }).select('id,item_id,season_id,started_on,finished_on,rating,comment').single();
    if (error) throw error;
    return {
      id: data.id, itemId: data.item_id, seasonId: data.season_id,
      startedOn: data.started_on, finishedOn: data.finished_on, rating: data.rating, comment: data.comment
    };
  }

  async deleteConsumption(id: number): Promise<void> {
    const { error } = await this.client.from('culture_consumptions').delete().eq('id', id);
    if (error) throw error;
  }

  async syncRaindrop(token: string, tag: string): Promise<number> {
    const { data, error } = await this.client.functions.invoke('raindrop-sync', {
      body: { token, tag, maxItems: 1000 }
    });
    if (error) throw error;
    if (!data || typeof data.imported !== 'number') throw new Error('Invalid sync response');
    return data.imported;
  }

  async listPlans(startDate: string, endDate: string): Promise<CulturePlan[]> {
    const { data, error } = await this.client.from('culture_plans')
      .select('id,calendar_date,list_id,bookmark_id,item_id,target_count,axis_id,position,completed_at')
      .gte('calendar_date', startDate).lte('calendar_date', endDate)
      .order('calendar_date');
    if (error) throw error;
    return (data ?? []).map(row => ({
      id: row.id, calendarDate: row.calendar_date, listId: row.list_id,
      bookmarkId: row.bookmark_id, itemId: row.item_id, targetCount: row.target_count, axisId: row.axis_id, position: row.position, completedAt: row.completed_at
    }));
  }

  async createPlan(input: Omit<CulturePlan, 'id'>): Promise<CulturePlan> {
    const { data: last, error: orderError } = await this.client.from('culture_plans')
      .select('position').eq('calendar_date',input.calendarDate)
      .eq('axis_id',input.axisId ?? -1)
      .order('position',{ascending:false}).limit(1);
    if (orderError) throw orderError;
    const position=(last?.[0]?.position ?? -1)+1;
    const { data, error } = await this.client.from('culture_plans').insert({
      position,
      calendar_date: input.calendarDate, list_id: input.listId,
      bookmark_id: input.bookmarkId, item_id: input.itemId ?? null, target_count: input.targetCount, axis_id: input.axisId ?? null
    }).select('id,calendar_date,list_id,bookmark_id,item_id,target_count,axis_id,position,completed_at').single();
    if (error) throw error;
    return {
      id: data.id, calendarDate: data.calendar_date, listId: data.list_id,
      bookmarkId: data.bookmark_id, itemId: data.item_id, targetCount: data.target_count,
      axisId: data.axis_id, position: data.position, completedAt: data.completed_at
    };
  }

  async selectDailyRaindrops(date: string, bookmarkIds: number[]): Promise<number> {
    const { data, error } = await this.client.rpc('culture_select_daily_raindrops', {
      p_date: date, p_bookmark_ids: bookmarkIds
    });
    if (error) throw error;
    return Number(data);
  }

  async releaseExpiredRaindropPlans(today: string): Promise<number> {
    const { data, error } = await this.client.rpc('culture_release_expired_raindrop_plans', { p_today: today });
    if (error) throw error;
    return Number(data ?? 0);
  }

  async setPlanAxis(planId: number, axisId: number | null): Promise<void> {
    const { data, error } = await this.client.from('culture_plans')
      .update({ axis_id: axisId })
      .eq('id', planId)
      .select('id')
      .single();
    if (error) throw error;
    if (!data) throw new Error('Culture plan missing');
  }

  async setCalendarPlanCompleted(id: number,completed: boolean): Promise<void> {
    const {error}=await this.client.rpc('culture_set_calendar_plan_completed',{p_id:id,p_completed:completed});
    if (error) throw error;
  }

  async replanCulturePlan(id: number, date: string, axisId: number | null, position: number): Promise<void> {
    const { data, error } = await this.client.from('culture_plans')
      .update({ calendar_date: date, axis_id: axisId, position })
      .eq('id', id).select('id').single();
    if (error) throw error;
    if (!data) throw new Error('Culture plan not found');
  }

  async moveCalendarPlan(id: number,direction: -1 | 1): Promise<void> {
    const { error } = await this.client.rpc('culture_move_calendar_plan',{p_id:id,p_direction:direction});
    if (error) throw error;
  }

  async deletePlan(id: number): Promise<void> {
    const { error } = await this.client.from('culture_plans').delete().eq('id', id);
    if (error) throw error;
  }

  async listListEntries(listId: number): Promise<CultureListEntry[]> {
    const { data, error } = await this.client.from('culture_list_entries')
      .select('id,list_id,position,item_id,season_id,bookmark_id')
      .eq('list_id', listId)
      .order('position')
      .order('id');
    if (error) throw error;
    return (data ?? []).map(row => ({
      id: row.id, listId: row.list_id, position: row.position,
      reference: row.item_id != null
        ? { kind: 'CULTURE_ITEM' as const, itemId: row.item_id }
        : row.season_id != null
          ? { kind: 'SERIES_SEASON' as const, seasonId: row.season_id }
          : { kind: 'BOOKMARK' as const, bookmarkId: row.bookmark_id }
    }));
  }

  async addListReference(listId: number, reference: CultureListReference): Promise<void> {
    const { data: current, error: listError } = await this.client.from('culture_list_entries')
      .select('position').eq('list_id', listId).order('position', { ascending: false }).limit(1);
    if (listError) throw listError;
    const position = (current?.[0]?.position ?? -1) + 1;
    const row = {
      list_id: listId,
      position,
      item_id: reference.kind === 'CULTURE_ITEM' ? reference.itemId : null,
      season_id: reference.kind === 'SERIES_SEASON' ? reference.seasonId : null,
      bookmark_id: reference.kind === 'BOOKMARK' ? reference.bookmarkId : null
    };
    const { error } = await this.client.from('culture_list_entries').insert(row);
    if (error) throw error;
  }

  async moveListReference(entryId: number, direction: -1 | 1): Promise<void> {
    const { error } = await this.client.rpc('culture_move_list_entry', {
      p_entry_id: entryId,
      p_direction: direction
    });
    if (error) throw error;
  }

  async removeListReference(entryId: number): Promise<void> {
    const { error } = await this.client.from('culture_list_entries').delete().eq('id', entryId);
    if (error) throw error;
  }

  async listBookmarksByIds(ids: number[]): Promise<CultureBookmark[]> {
    if (!ids.length) return [];
    const { data, error } = await this.client.from('culture_bookmarks')
      .select('id,title,url,type,source,external_id,collection_name,tags,status,completed_at')
      .in('id', ids);
    if (error) throw error;
    return (data ?? []).map(row => this.mapBookmark(row));
  }

  async listBookmarks(includeDone = false): Promise<CultureBookmark[]> {
    const pageSize = 500;
    const bookmarks: CultureBookmark[] = [];
    for (let offset = 0; ; offset += pageSize) {
      let query = this.client.from('culture_bookmarks')
        .select('id,title,url,type,source,external_id,collection_name,tags,status,completed_at');
      if (!includeDone) query = query.neq('status', 'DONE');
      const { data, error } = await query
        .order('created_at', { ascending: false })
        .order('id', { ascending: false })
        .range(offset, offset + pageSize - 1);
      if (error) throw error;
      const rows = data ?? [];
      bookmarks.push(...rows.map(row => this.mapBookmark(row)));
      if (rows.length < pageSize) break;
    }
    return bookmarks;
  }

  async upsertBookmark(input: BookmarkInput): Promise<CultureBookmark> {
    const record = {
      title: input.title, url: input.url, type: input.type, source: input.source,
      external_id: input.externalId ?? null, collection_name: input.collection ?? null,
      tags: input.tags ?? []
    };
    // Import from an external source is idempotent for the current account.
    // The partial unique index is not usable as a PostgREST ON CONFLICT target.
    if (input.externalId) {
      const { data: existing, error: lookupError } = await this.client.from('culture_bookmarks')
        .select('id').eq('source', input.source).eq('external_id', input.externalId).maybeSingle();
      if (lookupError) throw lookupError;
      if (existing) {
        const { data, error } = await this.client.from('culture_bookmarks').update(record)
          .eq('id', existing.id).select('id,title,url,type,source,external_id,collection_name,tags,status,completed_at').single();
        if (error) throw error;
        return this.mapBookmark(data);
      }
    }
    const { data, error } = await this.client.from('culture_bookmarks').insert(record)
      .select('id,title,url,type,source,external_id,collection_name,tags,status,completed_at').single();
    if (error) throw error;
    return this.mapBookmark(data);
  }

  async updateBookmark(id: number, input: BookmarkInput): Promise<void> {
    const { error } = await this.client.from('culture_bookmarks').update({
      title: input.title, url: input.url, type: input.type,
      collection_name: input.collection ?? null, tags: input.tags ?? []
    }).eq('id', id);
    if (error) throw error;
  }

  async updateBookmarkStatus(id: number, status: BookmarkStatus): Promise<void> {
    const { error } = await this.client.from('culture_bookmarks').update({
      status, completed_at: status === 'DONE' ? new Date().toISOString() : null
    }).eq('id', id);
    if (error) throw error;
  }

  async deleteBookmark(id: number): Promise<void> {
    const { error } = await this.client.from('culture_bookmarks').delete().eq('id', id);
    if (error) throw error;
  }

  async deleteRaindropBookmarks(ids: number[]): Promise<number> {
    const uniqueIds = [...new Set(ids)];
    if (!uniqueIds.length || uniqueIds.some(id => !Number.isSafeInteger(id) || id <= 0)) {
      throw new Error('Invalid Raindrop bookmark IDs');
    }
    let deleted = 0;
    for (let offset = 0; offset < uniqueIds.length; offset += 100) {
      const batch = uniqueIds.slice(offset, offset + 100);
      const { data, error } = await this.client.from('culture_bookmarks')
        .delete().eq('source', 'RAINDROP').in('id', batch).select('id');
      if (error) throw error;
      const count = data?.length ?? 0;
      deleted += count;
      if (count !== batch.length) throw new Error('Raindrop deletion incomplete');
    }
    return deleted;
  }

  private mapItem(row: { [key: string]: unknown }): CultureItem {
    return {
      id: Number(row['id']), type: row['type'] as CultureItem['type'], title: String(row['title']), source: row['source'] as string | null,
      externalId: row['external_id'] as string | null, createdAt: String(row['created_at']), updatedAt: String(row['updated_at'])
    };
  }

  private mapBookmark(row: { [key: string]: unknown }): CultureBookmark {
    return {
      id: Number(row['id']), title: String(row['title']), url: String(row['url']), type: row['type'] as CultureBookmark['type'], source: String(row['source']),
      externalId: row['external_id'] as string | null, collection: row['collection_name'] as string | null, tags: (row['tags'] ?? []) as string[],
      status: row['status'] as BookmarkStatus, completedAt: row['completed_at'] as string | null
    };
  }
}
